import { useState } from 'react';

export default function ReturnsPage() {
  const [filter, setFilter] = useState({ return_type: '', status: '' });

  const returns = [
    {
      return_id: 'M4-2025-10-NMF',
      institution: 'Northern Maple Financial',
      return_type: 'M4',
      return_name: 'Monthly Balance Sheet',
      period: '2025-10',
      submission_date: '2025-11-15',
      status: 'Submitted',
      validation_status: 'Passed',
      cet1_ratio: 0.048,
      flags: []
    },
    {
      return_id: 'BCAR-2025-Q3-NMF',
      institution: 'Northern Maple Financial',
      return_type: 'BCAR',
      return_name: 'Basel Capital Adequacy',
      period: '2025-Q3',
      submission_date: '2025-10-31',
      status: 'Submitted',
      validation_status: 'Passed',
      cet1_ratio: 0.048,
      flags: []
    },
    {
      return_id: 'LCR-2025-10-NMF',
      institution: 'Northern Maple Financial',
      return_type: 'LCR',
      return_name: 'Liquidity Coverage Ratio',
      period: '2025-10',
      submission_date: '2025-11-15',
      status: 'Submitted',
      validation_status: 'Passed',
      lcr_ratio: 1.33,
      flags: []
    },
    {
      return_id: 'E2-2025-Q3-NMF',
      institution: 'Northern Maple Financial',
      return_type: 'E2',
      return_name: 'Mortgage Portfolio',
      period: '2025-Q3',
      submission_date: '2025-10-31',
      status: 'Submitted',
      validation_status: 'Warning',
      gta_concentration: 0.42,
      flags: ['GTA concentration exceeds 35% threshold']
    },
    {
      return_id: 'N3-2025-Q3-NMF',
      institution: 'Northern Maple Financial',
      return_type: 'N3',
      return_name: 'Loan Arrears',
      period: '2025-Q3',
      submission_date: '2025-10-31',
      status: 'Submitted',
      validation_status: 'Passed',
      arrears_rate: 0.003,
      flags: []
    },
  ];

  const filtered = returns.filter(r =>
    (!filter.return_type || r.return_type === filter.return_type) &&
    (!filter.status || r.validation_status === filter.status)
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <p className="text-sm text-gray-600">Regulatory Returns Monitoring</p>
        <div className="mt-2 flex items-center space-x-2">
          <span className="text-sm text-gray-600">Institution:</span>
          <span className="font-semibold text-gray-900">Northern Maple Financial</span>
        </div>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <StatCard title="Returns Filed" value="5" subtitle="This month" color="blue" />
        <StatCard title="Validation Pass" value="4" subtitle="No issues" color="green" />
        <StatCard title="Warnings" value="1" subtitle="Under review" color="yellow" />
        <StatCard title="Overdue" value="0" subtitle="All current" color="gray" />
      </div>

      {/* Filters */}
      <div className="bg-white shadow-sm rounded-lg p-4 border border-gray-200">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Return Type</label>
            <select
              value={filter.return_type}
              onChange={(e) => setFilter({ ...filter, return_type: e.target.value })}
              className="w-full border border-gray-300 rounded px-3 py-2"
            >
              <option value="">All Types</option>
              <option value="M4">M4 - Monthly Balance Sheet</option>
              <option value="BCAR">BCAR - Basel Capital</option>
              <option value="LCR">LCR - Liquidity Coverage</option>
              <option value="E2">E2 - Mortgage Portfolio</option>
              <option value="N3">N3 - Loan Arrears</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
            <select
              value={filter.status}
              onChange={(e) => setFilter({ ...filter, status: e.target.value })}
              className="w-full border border-gray-300 rounded px-3 py-2"
            >
              <option value="">All Statuses</option>
              <option value="Passed">Passed</option>
              <option value="Warning">Warning</option>
              <option value="Failed">Failed</option>
            </select>
          </div>
          <div className="flex items-end">
            <button
              onClick={() => setFilter({ return_type: '', status: '' })}
              className="px-4 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300"
            >
              Clear Filters
            </button>
          </div>
        </div>
      </div>

      {/* Returns Table */}
      <div className="bg-white shadow-sm rounded-lg overflow-hidden border border-gray-200">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Return ID</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Period</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Submitted</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Validation</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Key Metric</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Flags</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {filtered.map((ret) => (
              <tr key={ret.return_id} className="hover:bg-gray-50">
                <td className="px-6 py-4 whitespace-nowrap text-sm font-mono text-blue-700">
                  {ret.return_id}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div>
                    <div className="text-sm font-medium text-gray-900">{ret.return_type}</div>
                    <div className="text-xs text-gray-500">{ret.return_name}</div>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{ret.period}</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{ret.submission_date}</td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className={`px-2 py-1 text-xs font-medium rounded ${
                    ret.validation_status === 'Passed' ? 'bg-green-100 text-green-800' :
                    ret.validation_status === 'Warning' ? 'bg-yellow-100 text-yellow-800' :
                    'bg-red-100 text-red-800'
                  }`}>
                    {ret.validation_status}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  {ret.cet1_ratio && `CET1: ${(ret.cet1_ratio * 100).toFixed(1)}%`}
                  {ret.lcr_ratio && `LCR: ${(ret.lcr_ratio * 100).toFixed(0)}%`}
                  {ret.gta_concentration && `GTA: ${(ret.gta_concentration * 100).toFixed(0)}%`}
                  {ret.arrears_rate && `Arrears: ${(ret.arrears_rate * 100).toFixed(2)}%`}
                </td>
                <td className="px-6 py-4 text-sm">
                  {ret.flags.length > 0 ? (
                    <div className="text-amber-600 text-xs">{ret.flags[0]}</div>
                  ) : (
                    <span className="text-gray-400">-</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* AI-Powered Anomaly Detection */}
      <div className="bg-gradient-to-r from-purple-50 to-blue-50 rounded-lg border border-purple-200 p-6">
        <div className="flex items-start space-x-3">
          <span className="text-3xl">🤖</span>
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">AI-Powered Compliance Monitoring</h3>
            <p className="text-sm text-gray-700 mb-3">
              Automated validation detected 1 anomaly requiring supervisor review:
            </p>
            <div className="bg-white rounded p-4 border border-gray-200">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <p className="text-sm font-medium text-gray-900 mb-1">
                    E2 Return: GTA Mortgage Concentration Risk
                  </p>
                  <p className="text-xs text-gray-600 mb-2">
                    Northern Maple Financial's residential mortgage exposure to Greater Toronto Area (42%)
                    exceeds prudent concentration threshold (35%). Peer median: 28%.
                  </p>
                  <p className="text-xs text-blue-700 font-medium">
                    → Recommendation: Schedule discussion on concentration risk mitigation strategy
                  </p>
                </div>
                <button className="ml-4 px-3 py-1 bg-blue-700 text-white rounded text-xs hover:bg-blue-800">
                  Review
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function StatCard({ title, value, subtitle, color }: any) {
  const colors = {
    blue: 'from-blue-500 to-blue-600',
    green: 'from-green-500 to-green-600',
    yellow: 'from-yellow-500 to-yellow-600',
    gray: 'from-gray-400 to-gray-500',
  };

  return (
    <div className="bg-white shadow-sm rounded-lg border border-gray-200 p-4">
      <div className={`bg-gradient-to-br ${colors[color as keyof typeof colors]} rounded-lg p-3 mb-3 inline-block`}>
        <span className="text-white text-2xl font-bold">{value}</span>
      </div>
      <p className="text-sm font-medium text-gray-900">{title}</p>
      <p className="text-xs text-gray-500">{subtitle}</p>
    </div>
  );
}
